import { useState, useEffect } from "react";
import { getAccreditations, adminApproveRequest, adminRejectRequest, adminBlockRequest, adminDeleteRequest, AccreditationRequest, getStrictAccreditationMode, setStrictAccreditationMode, refreshAccreditations } from "@/data/accreditationsStore";
import { useCitizensStore } from "@/data/citizensStore";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Shield, FileText, Users, MoreHorizontal, ShieldAlert, Trash2, Ban, MessageSquare, UserPlus, Bell, Download, Archive } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const AdminInscriptionsPage = () => {
  const [requests, setRequests] = useState<AccreditationRequest[]>([]);
  const { citizens, fetchCitizens, updateCitizenStatus, deleteCitizen } = useCitizensStore();
  const { toast } = useToast();
  const [strictMode, setStrictMode] = useState(getStrictAccreditationMode());

  const [isMessageDialogOpen, setIsMessageDialogOpen] = useState(false);
  const [messageCategory, setMessageCategory] = useState<"ALL" | "PARTY" | "ONG" | "INSTITUTION" | "MEDIA">("ALL");
  const [messageTargetMode, setMessageTargetMode] = useState<"ALL_IN_CATEGORY" | "SPECIFIC_PARTNER">("ALL_IN_CATEGORY");
  const [messagePartnerId, setMessagePartnerId] = useState("");
  const [messageContent, setMessageContent] = useState("");

  const [isManualRegDialogOpen, setIsManualRegDialogOpen] = useState(false);
  const [manualRegData, setManualRegData] = useState({ name: "", email: "", role: "PARTY" });

  const [mockMessageNotifications, setMockMessageNotifications] = useState([
    { id: "1", message: "Merci de mettre à jour vos documents d'accréditation avant le 30 mai.", targetName: "RNI", targetType: "PARTI", sentAt: "2026-05-06T09:00:00", readAt: "2026-05-06T10:15:00", isNew: true },
    { id: "2", message: "Veuillez vérifier vos identifiants de connexion.", targetName: "Association Al Baraka", targetType: "ONG", sentAt: "2026-05-05T08:00:00", readAt: "2026-05-05T14:30:00", isNew: false },
    { id: "3", message: "Rappel: Clôture des candidatures dans 2 jours.", targetName: "PAM", targetType: "PARTI", sentAt: "2026-05-06T08:30:00", readAt: "2026-05-06T11:05:00", isNew: true },
  ]);
  const [showNotifications, setShowNotifications] = useState(false);
  const [notifFilterCat, setNotifFilterCat] = useState("ALL");
  const unreadCount = mockMessageNotifications.filter(n => n.isNew).length;

  const filteredNotifs = mockMessageNotifications.filter(n => notifFilterCat === "ALL" || n.targetType === notifFilterCat);

  const exportNotifications = () => {
    let csv = "ID,Message,Destinataire,Categorie,Date Envoi,Date Lecture,Statut\n";
    filteredNotifs.forEach(n => {
      csv += `${n.id},"${n.message}",${n.targetName},${n.targetType},${new Date(n.sentAt).toLocaleString()},${new Date(n.readAt).toLocaleString()},${n.isNew ? 'Nouveau' : 'Lu'}\n`;
    });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", "historique_messages_dedies.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const openMessageDialog = () => {
    setMessageCategory("ALL");
    setMessageTargetMode("ALL_IN_CATEGORY");
    setMessagePartnerId("");
    setMessageContent("");
    setIsMessageDialogOpen(true);
  };

  const sendMessage = () => {
    if (!messageContent.trim()) {
      toast({ title: "Erreur", description: "Veuillez saisir un message.", variant: "destructive" });
      return;
    }
    if (messageCategory !== "ALL" && messageTargetMode === "SPECIFIC_PARTNER" && (!messagePartnerId || messagePartnerId === "none")) {
      toast({ title: "Erreur", description: "Veuillez sélectionner un partenaire précis.", variant: "destructive" });
      return;
    }

    let targetDesc = "tous les bénéficiaires d'Espaces Dédiés";
    if (messageCategory !== "ALL") {
      if (messageTargetMode === "ALL_IN_CATEGORY") {
        const catName = messageCategory === "PARTY" ? "Partis Politiques" : messageCategory === "ONG" ? "ONG" : messageCategory === "INSTITUTION" ? "Institutions" : "Médias";
        targetDesc = `tous les partenaires de la catégorie : ${catName}`;
      } else {
        const partnerName = requests.find(r => r.id === messagePartnerId)?.userName || "le partenaire sélectionné";
        targetDesc = `le partenaire : ${partnerName}`;
      }
    }

    toast({ title: "Message envoyé", description: `Message envoyé à ${targetDesc}.` });
    setIsMessageDialogOpen(false);
  };

  const loadData = () => {
    setRequests(getAccreditations());
    fetchCitizens();
  };

  useEffect(() => {
    refreshAccreditations();
    loadData();

    window.addEventListener("accreditationsChanged", loadData);

    // Listen for storage changes across tabs
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "kaynin_accreditations") {
        loadData();
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("accreditationsChanged", loadData);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const handleToggleStrictMode = (checked: boolean) => {
    setStrictMode(checked);
    setStrictAccreditationMode(checked);
    toast({
      title: checked ? "Mode Strict Activé" : "Mode Souple Activé",
      description: checked ? "Validation manuelle obligatoire pour débloquer les espaces." : "Les partenaires accèdent à leur espace dès l'inscription."
    });
  };

  const handleApprove = (req: AccreditationRequest) => {
    adminApproveRequest(req.id);
    toast({ title: "Délégué Certifié", description: `Le membre ${req.userName} a été certifié comme Délégué Officiel.` });
  };

  const handleReject = (req: AccreditationRequest) => {
    adminRejectRequest(req.id);
    toast({ title: "Demande Rejetée", description: "Le statut a été mis à jour." });
  };

  const handleBlock = (req: AccreditationRequest) => {
    adminBlockRequest(req.id);
    toast({ title: "Compte Bloqué", description: "Ce compte a été suspendu." });
  };

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold text-foreground flex items-center gap-2">
            <Users className="w-8 h-8 text-primary" />
            Gestion des Inscriptions
          </h1>
          <p className="text-muted-foreground mt-1">Gérez les inscriptions citoyennes et les accréditations des espaces dédiés.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant={showNotifications ? "secondary" : "outline"} className="gap-2 font-bold relative" onClick={() => setShowNotifications(!showNotifications)}>
            <Bell className="w-4 h-4" />
            Accusés de lecture
            {unreadCount > 0 && <span className="absolute -top-2 -right-2 bg-red-500 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full font-bold animate-pulse">{unreadCount}</span>}
          </Button>
          <Button variant="outline" className="gap-2 font-bold" onClick={openMessageDialog}>
            <MessageSquare className="w-4 h-4 text-primary" /> Message Espaces Dédiés
          </Button>
          <Button className="gap-2 font-bold bg-primary hover:bg-primary/90" onClick={() => setIsManualRegDialogOpen(true)}>
            <UserPlus className="w-4 h-4" /> Nouvelle Inscription Dédiée
          </Button>
        </div>
      </div>

      <Card className="border-border/50 shadow-sm bg-card mb-6">
        <CardContent className="p-4 flex items-center justify-between">
          <div className="flex flex-col space-y-1">
            <span className="font-semibold text-sm">Verrouillage Strict des Espaces Partenaires</span>
            <span className="text-xs text-muted-foreground">
              {strictMode
                ? "Activé : Validation manuelle obligatoire pour débloquer les espaces."
                : "Désactivé : Les partenaires accèdent à leur espace dès l'envoi du formulaire d'inscription."}
            </span>
          </div>
          <Switch checked={strictMode} onCheckedChange={handleToggleStrictMode} />
        </CardContent>
      </Card>

      {showNotifications && (
        <Card className="border-blue-200 shadow-sm animate-in slide-in-from-top-2 bg-blue-50/50">
          <CardHeader className="pb-2">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div>
                <CardTitle className="text-sm font-display flex items-center gap-2">
                  <Bell className="w-4 h-4 text-blue-600" /> Notifications & Accusés de lecture
                </CardTitle>
                <CardDescription>Suivi des messages administratifs lus par les partenaires</CardDescription>
              </div>
              <div className="flex gap-2 items-center flex-wrap">
                <Select value={notifFilterCat} onValueChange={setNotifFilterCat}>
                  <SelectTrigger className="w-[180px] h-8 text-xs bg-white"><SelectValue placeholder="Catégorie..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Toutes les catégories</SelectItem>
                    <SelectItem value="PARTI">Partis Politiques</SelectItem>
                    <SelectItem value="ONG">ONG / Associations</SelectItem>
                    <SelectItem value="INSTITUTION">Institutions Publiques</SelectItem>
                    <SelectItem value="MEDIA">Médias</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 bg-white hover:bg-muted" onClick={() => toast({ title: "Archivage réussi", description: "Les notifications du mois précédent ont été archivées.", variant: "default" })}>
                  <Archive className="w-3 h-3 text-muted-foreground" /> Archiver le mois
                </Button>
                <Button variant="outline" size="sm" className="h-8 text-xs gap-1 text-blue-600 border-blue-200 hover:bg-blue-100 bg-white" onClick={exportNotifications}>
                  <Download className="w-3 h-3" /> Exporter CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 max-h-[300px] overflow-y-auto pr-2">
              {[...filteredNotifs].sort((a, b) => (b.isNew === a.isNew ? 0 : b.isNew ? 1 : -1)).map(n => (
                <div key={n.id} className={`p-3 rounded-xl border flex flex-col gap-1 ${n.isNew ? 'bg-white border-blue-300 shadow-sm' : 'bg-muted/30 border-border/50 opacity-80'}`}>
                  <div className="flex justify-between items-start">
                    <p className="font-semibold text-sm">{n.targetName} <Badge variant="outline" className="ml-1 text-[10px] py-0">{n.targetType}</Badge></p>
                    <div className="flex items-center gap-2">
                      {n.isNew ? <Badge className="bg-red-500 text-white border-0 text-[10px]">Nouveau</Badge> : <Badge variant="secondary" className="text-[10px]">Lu</Badge>}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-6 w-6 p-0"><span className="sr-only">Actions</span><MoreHorizontal className="h-4 w-4 text-muted-foreground" /></Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => {
                            setMockMessageNotifications(prev => prev.map(item => item.id === n.id ? { ...item, isNew: false } : item));
                            toast({ description: "Message annulé/marqué comme lu." });
                          }}>
                            Annuler (Marquer lu)
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => {
                            setMockMessageNotifications(prev => prev.filter(item => item.id !== n.id));
                            toast({ description: "Message archivé." });
                          }}>
                            <Archive className="mr-2 h-4 w-4" /> Archiver
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => {
                            setMockMessageNotifications(prev => prev.filter(item => item.id !== n.id));
                            toast({ description: "Message supprimé." });
                          }}>
                            <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>
                  <p className="text-xs text-foreground italic">"{n.message}"</p>
                  <div className="flex justify-between items-center mt-1 border-t pt-1 border-border/50">
                    <p className="text-[10px] text-muted-foreground">Envoyé : {new Date(n.sentAt).toLocaleString()}</p>
                    <p className="text-[10px] text-emerald-600 font-semibold flex items-center gap-1"><CheckCircle className="w-3 h-3" /> Lu : {new Date(n.readAt).toLocaleString()}</p>
                  </div>
                </div>
              ))}
              {filteredNotifs.length === 0 && (
                <div className="text-center py-4 text-xs text-muted-foreground">Aucune notification pour cette catégorie.</div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="normales" className="w-full">
        <TabsList className="grid w-full max-w-[500px] grid-cols-2 mb-6">
          <TabsTrigger value="normales">Inscriptions Normales</TabsTrigger>
          <TabsTrigger value="dedies">Espaces Dédiés (Accréditations)</TabsTrigger>
        </TabsList>

        <TabsContent value="normales" className="mt-0 space-y-6">
          <Card className="border-border/50 shadow-sm bg-card">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Inscriptions "Grand Public"</CardTitle>
                <CardDescription>Liste des citoyens inscrits sur la plateforme</CardDescription>
              </div>
              <Badge variant="secondary" className="text-sm">{citizens.length} citoyens</Badge>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm text-left border-collapse">
                  <thead className="bg-muted/40 text-muted-foreground font-semibold uppercase text-xs">
                    <tr>
                      <th className="px-4 py-3 rounded-tl-lg">Nom</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Âge</th>
                      <th className="px-4 py-3">Résidence</th>
                      <th className="px-4 py-3">CSP</th>
                      <th className="px-4 py-3">Asso / Parti</th>
                      <th className="px-4 py-3">Date d'inscription</th>
                      <th className="px-4 py-3 rounded-tr-lg text-right">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {citizens.map((cit) => (
                      <tr key={cit.id} className="border-t border-border/50 hover:bg-muted/10 transition-colors">
                        <td className="px-4 py-3 font-medium flex items-center gap-2">
                          {cit.genre === 'femme' ? <span className="text-pink-600">♀</span> : <span className="text-blue-600">♂</span>}
                          {cit.name}
                          {(!cit.status || cit.status === 'valide') && <Badge className="ml-2 bg-emerald-100 text-emerald-800 border-none px-1.5"><CheckCircle className="w-3 h-3 mr-1" /> Validé</Badge>}
                          {cit.status === 'bloque' && <Badge className="ml-2 bg-red-100 text-red-800 border-none px-1.5"><XCircle className="w-3 h-3 mr-1" /> Bloqué</Badge>}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{cit.email}</td>
                        <td className="px-4 py-3">{cit.age}</td>
                        <td className="px-4 py-3 capitalize">{cit.residence}</td>
                        <td className="px-4 py-3 max-w-[150px] truncate" title={cit.categorieSociopro}>{cit.categorieSociopro}</td>
                        <td className="px-4 py-3">
                          <div className="flex flex-col gap-1 text-xs">
                            {cit.isAssociationMember ? (
                              <Badge variant="outline" className="bg-emerald-50 text-emerald-700 w-fit">Asso: {cit.associativeName || 'Oui'}</Badge>
                            ) : (
                              <span className="text-muted-foreground">Pas d'asso</span>
                            )}
                            {cit.affinitePolitique && cit.affinitePolitique !== 'none' ? (
                              <Badge variant="outline" className="bg-blue-50 text-blue-700 w-fit">Parti: {cit.affinitePolitique}</Badge>
                            ) : (
                              <span className="text-muted-foreground">Pas de parti</span>
                            )}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground">
                          {new Date(cit.registeredAt).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}
                        </td>
                        <td className="px-4 py-3 text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0"><span className="sr-only">Menu</span><MoreHorizontal className="h-4 w-4" /></Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {(!cit.status || cit.status === 'valide') ? (
                                <DropdownMenuItem className="text-amber-600" onClick={() => {
                                  updateCitizenStatus(cit.id, 'bloque');
                                  toast({ title: "Compte bloqué", description: `L'utilisateur ${cit.name} a été bloqué.` });
                                }}>
                                  <ShieldAlert className="mr-2 h-4 w-4" /> Bloquer l'accès
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem className="text-emerald-600" onClick={() => {
                                  updateCitizenStatus(cit.id, 'valide');
                                  toast({ title: "Compte validé", description: `L'utilisateur ${cit.name} a été réactivé.` });
                                }}>
                                  <CheckCircle className="mr-2 h-4 w-4" /> Valider (Débloquer)
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem className="text-destructive" onClick={() => {
                                if (window.confirm("Êtes-vous sûr de vouloir supprimer définitivement ce compte citoyen ?")) {
                                  deleteCitizen(cit.id);
                                  toast({ title: "Compte supprimé", description: `L'utilisateur ${cit.name} a été supprimé.`, variant: "destructive" });
                                }
                              }}>
                                <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                    {citizens.length === 0 && (
                      <tr>
                        <td colSpan={8} className="text-center py-8 text-muted-foreground">Aucun citoyen inscrit pour le moment.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dedies" className="mt-0 space-y-6">
          <Tabs defaultValue="partis" className="w-full">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-5 mb-6 h-auto">
              <TabsTrigger value="partis" className="py-2">Partis</TabsTrigger>
              <TabsTrigger value="independants" className="py-2">Indépendants</TabsTrigger>
              <TabsTrigger value="ong" className="py-2">ONG</TabsTrigger>
              <TabsTrigger value="institutions" className="py-2">Institutions</TabsTrigger>
              <TabsTrigger value="medias" className="py-2">Médias</TabsTrigger>
            </TabsList>

            <TabsContent value="partis" className="mt-0 space-y-6">
              <AccreditationsView
                requests={requests.filter(r => r.role === 'PARTY' && r.partyAcronym !== 'SAP' && r.documentUrl && r.documentUrl.trim() !== "")}
                onApprove={handleApprove}
                onReject={handleReject}
                onBlock={handleBlock}
              />
            </TabsContent>

            <TabsContent value="independants" className="mt-0 space-y-6">
              <AccreditationsView
                requests={requests.filter(r => r.role === 'PARTY' && r.partyAcronym === 'SAP' && r.documentUrl && r.documentUrl.trim() !== "")}
                onApprove={handleApprove}
                onReject={handleReject}
                onBlock={handleBlock}
              />
            </TabsContent>

            <TabsContent value="ong" className="mt-0 space-y-6">
              <AccreditationsView
                requests={requests.filter(r => r.role === 'ONG' && r.documentUrl && r.documentUrl.trim() !== "")}
                onApprove={handleApprove}
                onReject={handleReject}
                onBlock={handleBlock}
              />
            </TabsContent>

            <TabsContent value="institutions" className="mt-0 space-y-6">
              <AccreditationsView
                requests={requests.filter(r => r.role === 'INSTITUTION' && r.documentUrl && r.documentUrl.trim() !== "")}
                onApprove={handleApprove}
                onReject={handleReject}
                onBlock={handleBlock}
              />
            </TabsContent>

            <TabsContent value="medias" className="mt-0 space-y-6">
              <AccreditationsView
                requests={requests.filter(r => r.role === 'MEDIA' && r.documentUrl && r.documentUrl.trim() !== "")}
                onApprove={handleApprove}
                onReject={handleReject}
                onBlock={handleBlock}
              />
            </TabsContent>
          </Tabs>
        </TabsContent>
      </Tabs>

      <Dialog open={isMessageDialogOpen} onOpenChange={setIsMessageDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Message Administratif</DialogTitle>
            <DialogDescription>
              Sélectionnez les destinataires de ce message. Il s'affichera sous forme de notification avec accusé de lecture dans leur Espace Dédié.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Catégorie cible</Label>
              <Select value={messageCategory} onValueChange={(v: any) => { setMessageCategory(v); setMessageTargetMode("ALL_IN_CATEGORY"); setMessagePartnerId(""); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Tous les Espaces Dédiés</SelectItem>
                  <SelectItem value="PARTY">Partis Politiques</SelectItem>
                  <SelectItem value="ONG">ONG / Associations</SelectItem>
                  <SelectItem value="INSTITUTION">Institutions Publiques</SelectItem>
                  <SelectItem value="MEDIA">Médias</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {messageCategory !== "ALL" && (
              <div className="space-y-2">
                <Label>Portée du message</Label>
                <Select value={messageTargetMode} onValueChange={(v: any) => { setMessageTargetMode(v); setMessagePartnerId(""); }}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL_IN_CATEGORY">Toute la catégorie</SelectItem>
                    <SelectItem value="SPECIFIC_PARTNER">Un partenaire spécifique</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {messageCategory !== "ALL" && messageTargetMode === "SPECIFIC_PARTNER" && (
              <div className="space-y-2">
                <Label>Partenaire précis</Label>
                <Select value={messagePartnerId} onValueChange={setMessagePartnerId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un partenaire..." />
                  </SelectTrigger>
                  <SelectContent>
                    {requests.filter(r => r.status === 'approved' && r.role === messageCategory).length > 0 ? (
                      requests.filter(r => r.status === 'approved' && r.role === messageCategory).map(r => (
                        <SelectItem key={r.id} value={r.id}>{r.userName} ({r.partyAcronym || r.role})</SelectItem>
                      ))
                    ) : (
                      <SelectItem value="none" disabled>Aucun partenaire approuvé dans cette catégorie</SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label>Contenu du message</Label>
              <Textarea
                placeholder="Ex: Merci de mettre à jour vos documents d'accréditation avant le 30 mai..."
                value={messageContent}
                onChange={(e) => setMessageContent(e.target.value)}
                className="min-h-[120px]"
              />
            </div>
            <div className="bg-amber-50 text-amber-800 p-3 rounded-lg text-xs flex gap-2">
              <span className="text-lg leading-none">ℹ️</span>
              <p>Une fois lu, vous recevrez automatiquement une notification dans votre tableau de bord listant les utilisateurs ayant pris connaissance du message.</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsMessageDialogOpen(false)}>Annuler</Button>
            <Button onClick={sendMessage}>Envoyer le message</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isManualRegDialogOpen} onOpenChange={setIsManualRegDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Nouvelle Inscription Dédiée</DialogTitle>
            <DialogDescription>
              Création manuelle d'un compte avec Espace Dédié. Un email avec des identifiants provisoires sera envoyé à la structure.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Statut (Type d'Espace)</Label>
              <Select value={manualRegData.role} onValueChange={(v) => setManualRegData({ ...manualRegData, role: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PARTY">Parti Politique</SelectItem>
                  <SelectItem value="ONG">ONG / Association</SelectItem>
                  <SelectItem value="INSTITUTION">Institution Publique</SelectItem>
                  <SelectItem value="MEDIA">Média (Radio/TV)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Nom de la structure</Label>
              <input
                type="text"
                value={manualRegData.name}
                onChange={(e) => setManualRegData({ ...manualRegData, name: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                placeholder="Ex: Al Aoula, Association X..."
              />
            </div>
            <div className="space-y-2">
              <Label>Email de contact (Connexion)</Label>
              <input
                type="email"
                value={manualRegData.email}
                onChange={(e) => setManualRegData({ ...manualRegData, email: e.target.value })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background"
                placeholder="contact@structure.ma"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManualRegDialogOpen(false)}>Annuler</Button>
            <Button onClick={() => {
              if (!manualRegData.name || !manualRegData.email) return toast({ title: "Erreur", description: "Veuillez remplir tous les champs", variant: "destructive" });
              toast({ title: "Compte créé", description: `Le compte pour ${manualRegData.name} a été créé avec succès. L'email d'invitation a été envoyé.` });
              setIsManualRegDialogOpen(false);
              setManualRegData({ name: "", email: "", role: "PARTY" });
            }}>
              Créer le compte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

function AccreditationsView({
  requests,
  onApprove,
  onReject,
  onBlock
}: {
  requests: AccreditationRequest[],
  onApprove: (req: AccreditationRequest) => void,
  onReject: (req: AccreditationRequest) => void,
  onBlock: (req: AccreditationRequest) => void
}) {
  const pending = requests.filter(r => r.status === 'pending');
  const processed = requests.filter(r => r.status !== 'pending');

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      <Card className="border-border/50 shadow-sm bg-card">
        <CardHeader>
          <CardTitle className="text-lg flex justify-between">
            Nouvelles demandes ({pending.length})
            {pending.length > 0 && <span className="flex h-3 w-3 rounded-full bg-red-500 animate-ping"></span>}
          </CardTitle>
          <CardDescription>Demandes nécessitant votre attention</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {pending.length === 0 ? (
            <div className="p-8 text-center border rounded-xl border-dashed bg-muted/20">
              <Shield className="w-10 h-10 mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-muted-foreground">Aucune demande en attente.</p>
            </div>
          ) : (
            pending.map(req => (
              <div key={req.id} className="p-4 bg-background border rounded-xl flex flex-col gap-3">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-bold">{req.userName}</h4>
                    <p className="text-sm text-muted-foreground">Email : {req.email}</p>
                    {req.phone && <p className="text-sm text-muted-foreground">Tél : {req.phone}</p>}
                    {req.qualite && <p className="text-sm text-muted-foreground mt-1 text-primary/80">Qualité : {req.qualite}</p>}
                    <Badge className="mt-2 bg-primary/10 text-primary">{req.partyAcronym}</Badge>

                    <details className="mt-3 text-sm group">
                      <summary className="cursor-pointer text-blue-600 font-medium hover:underline text-xs outline-none">
                        <span className="group-open:hidden">▶ Voir le profil complet</span>
                        <span className="hidden group-open:inline">▼ Masquer le profil complet</span>
                      </summary>
                      <div className="mt-2 p-3 bg-muted/30 rounded-lg border border-border/50 text-xs text-muted-foreground grid grid-cols-2 gap-2">
                        {req.age && <div><strong className="text-foreground">Âge :</strong> {req.age}</div>}
                        {req.genre && <div><strong className="text-foreground">Genre :</strong> {req.genre}</div>}
                        {req.residence && <div><strong className="text-foreground">Résidence :</strong> {req.residence}</div>}
                        {req.categorieSociopro && <div className="col-span-2"><strong className="text-foreground">Catégorie Socio-pro :</strong> {req.categorieSociopro}</div>}
                        {req.affinitePolitique && <div className="col-span-2"><strong className="text-foreground">Affinité Politique :</strong> {req.affinitePolitique}</div>}
                        {req.metadata && (() => {
                          try {
                            const meta = JSON.parse(req.metadata);
                            return Object.entries(meta).map(([k, v]) => (
                              <div key={k} className="col-span-2"><strong className="text-foreground capitalize">{k.replace(/([A-Z])/g, ' $1')} :</strong> {String(v)}</div>
                            ));
                          } catch (e) { return null; }
                        })()}
                        {(!req.age && !req.genre && !req.residence && !req.categorieSociopro && !req.affinitePolitique && !req.metadata) && (
                          <div className="col-span-2 italic opacity-50">Aucune information démographique supplémentaire.</div>
                        )}
                      </div>
                    </details>
                  </div>
                  <div>
                    <button onClick={() => {
                      const a = document.createElement('a');
                      a.href = req.documentUrl || '#';
                      a.download = `Accreditation_${req.userName.replace(/\s+/g, '_')}.pdf`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                    }} className="flex shrink-0 items-center gap-1 text-xs font-semibold text-blue-600 bg-blue-50 px-3 py-1.5 rounded-md hover:bg-blue-100 transition-colors">
                      <FileText className="w-4 h-4" /> Télécharger
                    </button>
                  </div>
                </div>
                <div className="pt-2 border-t flex flex-wrap justify-end gap-2">
                  <Button variant="outline" className="text-destructive border-red-200 hover:bg-red-50" onClick={() => onReject(req)}>
                    <XCircle className="w-4 h-4 mr-1" /> Rejeter
                  </Button>
                  <Button variant="outline" className="text-orange-600 border-orange-200 hover:bg-orange-50" onClick={() => onBlock(req)}>
                    <Ban className="w-4 h-4 mr-1" /> Bloquer
                  </Button>
                  <Button className="gap-2 bg-emerald-600 hover:bg-emerald-700" onClick={() => onApprove(req)}>
                    <CheckCircle className="w-4 h-4" /> Certifier
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground italic text-right mt-1">Code envoyé au candidat: {req.emailCode}</p>
              </div>
            ))
          )}
        </CardContent>
      </Card>

      <Card className="border-border/50 shadow-sm bg-card">
        <CardHeader>
          <CardTitle className="text-lg">Historique ({processed.length})</CardTitle>
          <CardDescription>Demandes traitées ou certifiées par e-mail</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {processed.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">Historique vide.</div>
          ) : (
            processed.map(req => (
              <div key={req.id} className="p-3 bg-muted/20 border rounded-lg flex justify-between items-center flex-wrap gap-2">
                <div>
                  <p className="font-semibold text-sm">{req.userName} <span className="text-xs text-muted-foreground">({req.partyAcronym})</span></p>
                  <p className="text-xs text-muted-foreground">{new Date(req.createdAt).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => {
                    const a = document.createElement('a');
                    a.href = req.documentUrl || '#';
                    a.download = `Accreditation_${req.userName.replace(/\s+/g, '_')}.pdf`;
                    document.body.appendChild(a);
                    a.click();
                    document.body.removeChild(a);
                  }} className="flex shrink-0 items-center gap-1 text-[10px] font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded hover:bg-blue-100 transition-colors">
                    <FileText className="w-3 h-3" />
                    <span className="hidden sm:inline">Télécharger</span>
                  </button>
                  {req.status === 'approved' ? (
                    <Badge className="bg-emerald-100 text-emerald-800"><CheckCircle className="w-3 h-3 mr-1" /> Validé</Badge>
                  ) : req.status === 'blocked' ? (
                    <Badge className="bg-orange-100 text-orange-800"><Ban className="w-3 h-3 mr-1" /> Bloqué</Badge>
                  ) : (
                    <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" /> Rejeté</Badge>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="sm" className="h-6 w-6 p-0 ml-1"><MoreHorizontal className="h-4 w-4 text-muted-foreground" /></Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {req.status !== 'approved' && (
                        <DropdownMenuItem className="text-emerald-600" onClick={() => onApprove(req)}>
                          <CheckCircle className="mr-2 h-4 w-4" /> Activer
                        </DropdownMenuItem>
                      )}
                      {req.status !== 'blocked' && (
                        <DropdownMenuItem className="text-orange-600" onClick={() => onBlock(req)}>
                          <Ban className="mr-2 h-4 w-4" /> Bloquer
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem className="text-destructive" onClick={() => {
                        if (window.confirm("Supprimer cette accréditation ?")) {
                          adminDeleteRequest(req.id);
                        }
                      }}>
                        <Trash2 className="mr-2 h-4 w-4" /> Supprimer
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default AdminInscriptionsPage;
